
import Vue from 'vue';
import _keyBy from 'lodash/keyBy';
import _omit from 'lodash/omit';
import moment from 'moment-timezone';
import pluralize from 'pluralize';
import { CompanyCustomFieldDef, DbTimestamp, Team } from '@run-diffusion/shared';
import { mapActions, mapGetters, mapState } from 'vuex';
import { Timestamp } from '@/firebase';
import { CursorListMixin, FiltersPanelMixin, JustAddedRowsMixin, TeamUserMixin } from '@/mixins';
import { JUST_ADDED_LIST } from '@/utils/justAdded';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { StoredLastPagination } from '@/store';
import { ListFetchResult, restartWalk, walkPageNumber } from '@/utils/cursorWalk';
import { HydratedListView, hydrateListView, rememberListView } from '@/utils/listView';
import { normalizePanelFilters } from '@/utils/panelFilters';
import { TEAMS_API_BASE_URL, TeamListRow, fetchTeamsPage } from '@/utils/teamsApi';
import { MirrorLagChannel } from '@/utils/mirrorLagChannel';
import { TEAMS_ADDED, noteTeamsChanged } from '@/utils/teamsMirrorLag';
import { fetchTeamsById, withFreshTeams } from '@/services/teamDocs';
import {
	COMPANY_TEAMS_RESTING_FILTERS,
	CompanyTeamsPanelFilters,
	TEAMS_DEFAULT_OPTIONS,
	TEAMS_HEADER_VALUE,
	TEAM_ACTIVATION,
	TEAM_ACTIVATION_ITEMS,
	TEAM_CUSTOM_FIELD_HEADER_PREFIX,
	TEAM_TABLES_ITEMS_PER_PAGE_OPTIONS,
	TableHeaderValue,
	TeamDownloadRow,
	buildCompanyTeamsRequest,
	isSortableTeamsHeader,
	stubTeamFromRow,
	teamDownloadHeaders,
	teamDownloadRow,
	teamTablesDownloadSort,
} from '@/views/Team/teamTablesState';
import TeamRow from '@/views/Team/CompanyDashboard/TeamRow.vue';
import TableBulkActionsPopover from '@/components/base/TableBulkActionsPopover.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import LiveDoc from '@/components/LiveDoc.vue';
import TableToolbar from '@/components/Filters/TableToolbar.vue';
import FiltersPanel from '@/components/Filters/FiltersPanel.vue';
import CompanyMemberPickerField from '@/components/Filters/CompanyMemberPickerField.vue';

/**
 * The company's Teams on its dashboard, the way the Directory lists people:
 * Postgres (through api2's `/teams`) picks and orders each page within what the
 * viewer may see, filtered by the search and the Filters box, and every row
 * binds its own Team and stays live (LiveDoc). Tokens used, balances and budgets
 * are never Postgres's: those cells read the live documents, and the CSV leaves
 * them out. Active or Deactivated is the `?deactivated` route query, which the
 * cards above read too, so the box's Activation field writes the route. Changes go
 * through the teams mirror-lag channels (`teamsMirrorLag.ts`): the cards above
 * catch up with every one once the mirror has it, and this table with a new Team.
 * An edited or deactivated Team stays where it is, live, until Refresh. Row and
 * bulk actions hand TeamPage's dialogs the Teams read fresh from Firestore, never
 * a row's stand-in.
 */
export default Vue.extend({
	name: 'TeamsManagement',
	mixins: [
		TeamUserMixin,
		CursorListMixin,
		FiltersPanelMixin,
		JustAddedRowsMixin,
	],
	data () {
		return {
			options: { ...TEAMS_DEFAULT_OPTIONS },
			itemsPerPageOptions: TEAM_TABLES_ITEMS_PER_PAGE_OPTIONS,
			searchQuery: '',
			// What the listing was fetched with, but for Activation, which is the route's
			appliedFilters: { ...COMPANY_TEAMS_RESTING_FILTERS } as CompanyTeamsPanelFilters,
			hydratedCompanyId: null as string | null,
			// The half the rows on screen were fetched in. The route can move on before the
			// rows do (a new Activation, leaving the page), and the rows answer to their own.
			listedActivation: TEAM_ACTIVATION.ACTIVE as string,
			// Kept across pages; a new search, filter or company clears it.
			selectedRows: [] as TeamListRow[],
			loadingBulkTeams: false,
			activationFilterItems: TEAM_ACTIVATION_ITEMS,
		};
	},
	computed: {
		...mapState([
			'user',
			'companyDashboard',
			'team',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		companyId (): string | null {
			return this.team?.companyId || null;
		},
		teamCustomFieldDefs (): CompanyCustomFieldDef[] {
			return this.team?.company?.teamCustomFieldDefs || [];
		},
		customFieldKeys (): string[] {
			return this.teamCustomFieldDefs.map((fieldDef: CompanyCustomFieldDef) => fieldDef.key);
		},
		searchPlaceholder (): string {
			const customField: string = this.teamCustomFieldDefs.length ? `, ${this.teamCustomFieldDefs[0].label}` : '';
			return `Search by ${this.displayLabels.team} name${customField}...`;
		},
		showingDeactivated (): boolean {
			return !!this.$route.query.deactivated;
		},
		// CursorListMixin: a Team or membership added anywhere in the app
		listChangeChannel (): MirrorLagChannel {
			return TEAMS_ADDED;
		},
		// JustAddedRowsMixin: a Team created here shows on top before the listing has it
		justAddedListKey (): string | null {
			return this.companyId ? JUST_ADDED_LIST.companyTeams(this.companyId) : null;
		},
		// FiltersPanelMixin
		restingPanelFilters (): CompanyTeamsPanelFilters {
			return COMPANY_TEAMS_RESTING_FILTERS;
		},
		appliedPanelFilters (): CompanyTeamsPanelFilters {
			return {
				...this.appliedFilters,
				activation: this.showingDeactivated ? TEAM_ACTIVATION.DEACTIVATED : TEAM_ACTIVATION.ACTIVE,
			};
		},
		// CursorListMixin
		listingReady (): boolean {
			return !!this.companyId;
		},
		listingPending (): boolean {
			return !this.team;
		},
		listFetchFailureMessages (): Record<string, string> {
			const teams: string = pluralize(this.displayLabels.team);
			return {
				unauthorized: `Your session has expired, sign in again to view ${teams}`,
				forbidden: `You do not have access to this company's ${teams}`,
				network: 'Could not reach the server, check your connection and try again',
				failed: `Could not load ${teams}, please try again`,
			};
		},
		headers () {
			return [
				{
					text: this.displayLabels.team,
					value: TEAMS_HEADER_VALUE.NAME,
					align: 'left',
					class: 'py-0 pl-0 pr-1',
					sortable: true,
				},
				...(this.teamCustomFieldDefs.map((fieldDef: CompanyCustomFieldDef) => ({
					text: fieldDef.label,
					value: `${TEAM_CUSTOM_FIELD_HEADER_PREFIX}${fieldDef.key}`,
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				}))),
				...(!this.showingDeactivated && (this.hasShowTokenBalancePermission || this.hasManageCompanyPermission) ? [
					{
						noDownload: true,
						// Enterprise teams count up (TeamRow shows what the cycle spent), everyone else holds a balance
						text: this.companyClubOfferBenefits.isEnterprise ? 'Tokens Used' : 'Token Balance',
						value: 'tokens',
						align: 'left',
						class: 'py-0 px-1',
						sortable: false,
					},
					...(this.companyClubOfferBenefits.isEnterprise && this.hasManageCompanyPermission ? [
						{
							noDownload: true,
							text: 'Monthly Budget',
							value: 'monthlyBudget',
							align: 'left',
							class: 'py-0 px-1',
							sortable: false,
						},
						{
							noDownload: true,
							text: 'Budget Setting',
							value: 'budgetSetting',
							align: 'left',
							class: 'py-0 px-1',
							sortable: false,
						},
					] : []),
				] : []),
				{
					noDownload: true,
					text: 'Members',
					value: TEAMS_HEADER_VALUE.MEMBERS,
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				},
				...(this.hasManageTeamsPermission || this.hasManageCompanyPermission ? [
					{
						noDownload: true,
						text: 'Actions',
						value: TEAMS_HEADER_VALUE.ACTIONS,
						align: 'right',
						class: 'py-0 pr-4',
						sortable: false,
					},
				] : []),
			];
		},
		// The CSV is built from the Postgres rows alone: names, custom fields and counts, never money.
		downloadRows (): TeamDownloadRow[] {
			return this.selectedRows.map((row: TeamListRow) => teamDownloadRow(row));
		},
		// The Team and its custom fields; the member counts stay in the table
		downloadHeaders (): TableHeaderValue[] {
			return teamDownloadHeaders(this.selectedRows, this.displayLabels.team, []);
		},
		// Opens with the company's name, as the Directory's does
		downloadFileName (): string {
			const companyName: string = this.team?.company?.name || '';
			const listName: string = `${this.displayLabels.team}-list`;
			return companyName ? `${companyName}-${listName}` : listName;
		},
		downloadSort (): { sortBy: string[]; sortDesc: boolean[] } {
			return teamTablesDownloadSort(this.options, [TEAMS_HEADER_VALUE.NAME, TEAMS_HEADER_VALUE.MEMBERS]);
		},
		isNarrowed (): boolean {
			// Activation is not narrowing: it picks the half the title names
			return !!this.searchQuery || this.appliedFilters.memberEmails.length > 0;
		},
		emptyStateMessage (): string {
			const teams: string = pluralize(this.displayLabels.team);
			if (this.failed) return `Could not load ${teams}`;
			if (this.isNarrowed) return `No ${teams} match your search or filters`;
			if (this.showingDeactivated) return `No deactivated ${teams}`;
			return `No ${teams} yet, create some!`;
		},
	},
	watch: {
		// Another company is another listing: its own remembered view, nothing selected, from the top.
		companyId: {
			immediate: true,
			handler (newVal: string | null, oldVal: string | null) {
				if (newVal === oldVal) return;
				this.clearSelectedRows();
				this.walk = restartWalk();
				this.hydrateView(newVal);
				this.scheduleFetch();
			},
		},
		// Active or Deactivated changes with the route, Apply's or the browser's back and forward
		showingDeactivated (newVal: boolean, oldVal: boolean) {
			if (newVal === oldVal) return;
			this.clearSelectedRows();
			this.draftFilters = { ...this.draftFilters, activation: this.appliedPanelFilters.activation };
			this.resetToFirstPage();
		},
	},
	beforeDestroy () {
		if (this.companyId) {
			this.storeLastPagination({
				key: 'lastCompanyPagination',
				// With the half it was taken in, so a cursor is only resumed there
				pagination: rememberListView(this.options, this.searchQuery, { ...this.appliedFilters, activation: this.listedActivation }, this.walk.cursor, walkPageNumber(this.walk)),
				companyId: this.companyId,
			});
		}
	},
	methods: {
		...mapActions([
			'storeLastPagination',
			'setUpsellDialog',
			'updateSnackbar',
		]),
		pluralize,
		hydrateView (companyId: string | null) {
			if (!companyId || this.hydratedCompanyId === companyId) return;
			this.hydratedCompanyId = companyId;
			const stored: StoredLastPagination | undefined = this.companyDashboard?.lastCompanyPagination?.[companyId];
			const view: HydratedListView<CompanyTeamsPanelFilters> = hydrateListView(
				stored,
				TEAMS_DEFAULT_OPTIONS,
				COMPANY_TEAMS_RESTING_FILTERS,
				{
					isSortable: (header: string) => isSortableTeamsHeader(header, this.customFieldKeys),
					itemsPerPageOptions: TEAM_TABLES_ITEMS_PER_PAGE_OPTIONS,
				},
			);
			this.options = view.options;
			this.searchQuery = view.searchQuery || '';
			// Activation stays the route's: a link to the deactivated half must land there
			this.appliedFilters = { ...(view.filters || COMPANY_TEAMS_RESTING_FILTERS), activation: COMPANY_TEAMS_RESTING_FILTERS.activation };
			// The position belongs to the half it was taken in, which the route may not be showing now
			const storedActivation: unknown = stored && stored.filters ? stored.filters.activation : undefined;
			const sameHalf: boolean = (storedActivation === TEAM_ACTIVATION.DEACTIVATED) === this.showingDeactivated;
			this.walk = sameHalf ? view.walk : restartWalk();
			this.syncFiltersPanel();
		},
		fetchListPage ({ idToken, cursor }: { idToken: string; cursor: string | null }): Promise<ListFetchResult<TeamListRow>> {
			this.listedActivation = this.appliedPanelFilters.activation;
			return fetchTeamsPage({
				baseUrl: TEAMS_API_BASE_URL,
				idToken,
				request: buildCompanyTeamsRequest({
					companyId: this.companyId,
					accountTeamId: this.team?.id || null,
					cursor,
					options: this.options,
					searchQuery: this.searchQuery,
					filters: this.appliedPanelFilters,
				}),
			});
		},
		// A selected Team back on screen is selected as this copy, so the CSV reads the newer one.
		onListRowsShown (rows: TeamListRow[]) {
			const shownById: Record<string, TeamListRow> = _keyBy(rows, 'id');
			if (this.selectedRows.some((row: TeamListRow) => shownById[row.id] && shownById[row.id] !== row)) {
				this.selectedRows = this.selectedRows.map((row: TeamListRow) => shownById[row.id] || row);
			}
		},
		/**
		 * Apply and Clear all land here. The member's email is the
		 * table's; Activation is the route's, so a change there goes through the
		 * route, whose watcher starts the walk over.
		 */
		commitPanelFilters (next: CompanyTeamsPanelFilters) {
			const clean: CompanyTeamsPanelFilters = normalizePanelFilters(COMPANY_TEAMS_RESTING_FILTERS, next);
			this.clearSelectedRows();
			this.appliedFilters = { ...clean, activation: COMPANY_TEAMS_RESTING_FILTERS.activation };
			const wantDeactivated: boolean = clean.activation === TEAM_ACTIVATION.DEACTIVATED;
			if (wantDeactivated !== this.showingDeactivated) {
				this.routerReplace(this.$route, this.$router, {
					name: this.$route.name,
					params: this.$route.params,
					query: {
						..._omit(this.$route.query, 'deactivated'),
						...(wantDeactivated && { deactivated: '1' }),
					},
				});
				return;
			}
			this.resetToFirstPage();
		},
		setSearchQuery (query: string) {
			const next: string = query || '';
			if (next === this.searchQuery) return;
			this.clearSelectedRows();
			this.searchQuery = next;
			this.resetToFirstPage();
		},
		clearSelectedRows () {
			this.$nextTick(() => {
				// Next tick seems to prevent issue with check boxes getting in a state where they're unselectable after a bulk update and clear selected items operation
				this.selectedRows = [];
			});
		},
		stubFor (row: TeamListRow): Team {
			return stubTeamFromRow(
				row,
				this.companyId,
				(iso: string): DbTimestamp => Timestamp.fromMillis(moment(iso).valueOf()) as DbTimestamp,
			);
		},
		onNewTeamClick () {
			if (this.companyClubOfferBenefits.isEnterprise) {
				this.reemitWithRefetch('new-team', {});
			} else {
				this.setUpsellDialog({ noMultiTeams: true });
			}
		},
		// A change made here: the cards above catch up once the mirror has it. TeamPage says
		// on its own which changes are additions, which this list catches up with too.
		onTeamsChanged () {
			this.clearSelectedRows();
			noteTeamsChanged();
		},
		// A row's Team was deleted: never act on it, and ask again later.
		onRowRemoved (teamId: string) {
			this.selectedRows = this.selectedRows.filter((row: TeamListRow) => row.id !== teamId);
			this.forgetJustAddedRow(teamId);
			this.refetchAfterMirrorLag();
		},
		/**
		 * A row's own action. The row may still be showing its Postgres stand-in, which
		 * has no budgets, or a live copy a moment old, so the dialog gets the Teams
		 * read fresh: editing a stand-in would save its blanks over the real budget.
		 */
		async onRowAction (eventName: string, payload: Record<string, unknown>) {
			let fresh: Record<string, unknown> | null;
			try {
				fresh = await withFreshTeams(payload);
			} catch (error) {
				console.error('Error loading the team:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Could not load this ${this.displayLabels.team}, please try again`,
					show: true,
				});
				return;
			}
			if (this.isDestroyed) return;
			if (!fresh) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `This ${this.displayLabels.team} is no longer available`,
					show: true,
				});
				return;
			}
			this.reemitWithRefetch(eventName, fresh);
		},
		/**
		 * An action, passed up to TeamPage (which owns the dialogs) with an
		 * onSuccess that clears the selection and lets the cards above catch up
		 * (and this list, when TeamPage says it was a new Team).
		 */
		reemitWithRefetch (eventName: string, payload: Record<string, unknown>) {
			this.$emit(eventName, {
				...payload,
				onSuccess: () => this.onTeamsChanged(),
			});
		},
		/**
		 * The bulk Budgets, Deactivate and Reactivate edit the Teams themselves, so
		 * the dialogs get them as they are now, read by id, rather than the
		 * mirror's rows, which can be a few seconds behind and may span pages.
		 */
		async onBulkTeams (eventName: 'edit-teams' | 'deactivate-teams' | 'activate-teams') {
			if (!this.selectedRows.length || this.loadingBulkTeams) return;
			this.loadingBulkTeams = true;
			try {
				const teams: Team[] = await fetchTeamsById(this.selectedRows.map((row: TeamListRow) => row.id));
				if (this.isDestroyed) return;
				if (!teams.length) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `The selected ${pluralize(this.displayLabels.team)} are no longer available`,
						show: true,
					});
					return;
				}
				this.reemitWithRefetch(eventName, {
					teams,
					// EditTeams lists the selected Teams in the table's order
					...(eventName === 'edit-teams' && { pagination: this.options }),
				});
			} catch (error) {
				console.error('Error loading the selected teams:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Could not load the selected ${pluralize(this.displayLabels.team)}, please try again`,
					show: true,
				});
			} finally {
				this.loadingBulkTeams = false;
			}
		},
	},
	components: {
		BaseButton,
		CompanyMemberPickerField,
		EmptyState,
		FiltersPanel,
		LiveDoc,
		TableBulkActionsPopover,
		TableToolbar,
		TeamRow,
	},
});
